import React from 'react';
import cx from 'classnames';
import CloseIcon from '@icons/CloseIcon';
import ReactPortal from '@primitives/ReactPortal';
import Typography from '@primitives/Typography';
import styles from './ModalDialog.module.scss';
import { ModalDialogProps } from './types';
import Button from '@primitives/Button';
import IconButton from '@primitives/IconButton';
import { useKeyListener } from '@hooks';
import { ESCAPE_KEY } from '@constants/Components';

const ModalDialog: React.FC<ModalDialogProps> = ({
    open,
    onClose,
    children,
    footerButtons,
    header,
    customFooter,
    contentClasses,
    dialogClass = '',
    dialogContainerClass = '',
    preventCloseOnOutsideClick = false,
    customHeader,
    ...restProps
}) => {
    const dialogContainerStyles = cx(styles['dialog-container'], dialogContainerClass, {
        [styles['is-open']]: open,
    });

    const handleClose = (): void => {
        onClose();
    };

    const handleModalOutSideClick = (): void => {
        if (!preventCloseOnOutsideClick) handleClose();
    };

    const renderHeader = (): JSX.Element => {
        const headerStyles = cx(styles.header, {
            [styles['header--without-text']]: !header,
        });

        return (
            <div className={headerStyles} role="heading">
                {customHeader ||
                    (header && (
                        <Typography variant="h3" color={styles.modalDialogHeaderFontColor}>
                            {header}
                        </Typography>
                    ))}
                <IconButton
                    Icon={CloseIcon}
                    size="medium"
                    onClick={handleClose}
                    variant="borderless-secondary"
                    data-testid="close-icon"
                />
            </div>
        );
    };

    const renderFooter = (): JSX.Element | false | undefined => {
        if (customFooter) return customFooter;
        return (
            !!footerButtons?.length && (
                <div className={styles.footer}>
                    {footerButtons.map((cta, index) => {
                        const { label, ...restProps } = cta;
                        return (
                            <Button
                                variant={
                                    (footerButtons.length - 1 - index) % 2 === 0
                                        ? 'primary'
                                        : 'secondary'
                                }
                                key={label}
                                {...restProps}
                                role="button"
                            >
                                {label}
                            </Button>
                        );
                    })}
                </div>
            )
        );
    };

    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';

    useKeyListener(ESCAPE_KEY, handleClose, open);

    return (
        <ReactPortal wrapperId="dialogPortal">
            <main
                className={dialogContainerStyles}
                onClick={handleModalOutSideClick}
                data-testid="overlay"
            >
                <section
                    className={cx(styles.dialog, dialogClass)}
                    onClick={(e) => e.stopPropagation()}
                    {...restProps}
                >
                    {renderHeader()}
                    <section className={cx(styles['dialog-content'], contentClasses)}>
                        {children}
                    </section>
                    {renderFooter()}
                </section>
            </main>
        </ReactPortal>
    );
};

export default ModalDialog;
